import { alpha } from '@mui/system';
import * as yup from 'yup';
import { AddDictionarySvg, BulkGlossaryUploadIcon } from '../../assets';
import { regExIdentifiers, validationMessages } from '../../constants';
import { GLOSSARY_METADATA_PATHS } from '../../constants/glossary-metadata-paths';
import {
    noSpaceAllowedInSchemaTest,
    themeObject,
    validationsMessages,
} from '../../lib';
import { AttributeAssociationItem } from '../edge-definitions/edge-definition-helper';

export const tableHeight: number = 210;

export interface GlossaryMetadataItem {
    term: string;
    id?: string;
    description?: string;
    groupName?: string;
    type?: string;
    abbreviations?: string[];
    aliases?: string[];
    properties?: AttributeAssociationItem[];
}

export type GlossaryTableColumns = {
    field: string;
    headerName: string;
    width?: number;
    editable?: boolean;
    flex?: number;
    headerClassName?: string;
};

export const glossaryValidationMessages = {
    duplicatedTerm: 'Term already exists. Please choose another term.',
    duplicatedOptions: 'Field cannot have duplicate values',
};

export const getGlossaryMetadataColumns = (): GlossaryTableColumns[] => {
    return [
        {
            field: 'term',
            headerName: 'Term',
            width: 150,
            editable: false,
            flex: 1,
            headerClassName: 'dictionary-grid-header',
        },
        {
            field: 'type',
            headerName: 'Type',
            editable: false,
            flex: 1,
            headerClassName: 'dictionary-grid-header',
        },
        {
            field: 'description',
            headerName: 'Description',
            editable: false,
            flex: 1,
            headerClassName: 'dictionary-grid-header',
        },
        {
            field: 'groupName',
            headerName: 'Group Name',
            editable: false,
            flex: 1,
            headerClassName: 'dictionary-grid-header',
        },
    ];
};

export const defaultGlossary: GlossaryMetadataItem = {
    term: '',
    description: '',
    groupName: '',
    type: '',
};

export const glossaryMetadataSchema: yup.SchemaOf<GlossaryMetadataItem> =
    yup.object({
        term: noSpaceAllowedInSchemaTest(
            yup
                .string()
                .required(validationsMessages.required)
                .matches(regExIdentifiers.name, validationMessages.invalidName),
            validationsMessages.required
        ).required(),
        id: yup.string(),
        description: noSpaceAllowedInSchemaTest(
            yup.string(),
            validationsMessages.required
        ).optional(),
        groupName: noSpaceAllowedInSchemaTest(
            yup.string(),
            validationsMessages.required
        ).optional(),
        type: noSpaceAllowedInSchemaTest(
            yup.string(),
            validationsMessages.required
        ).optional(),
        abbreviations: yup.array().of(yup.string()).optional(),
        aliases: yup.array().of(yup.string()).optional(),
        properties: yup.array().optional(),
    });

export const glossaryMetadataFormStyles = {
    formContainer: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        mt: 2.5,
        mx: '143.5px',
    },
    generalInfoContainer: {
        backgroundColor: themeObject.palette.background.default,
        border: `1px solid ${themeObject.palette.grayScale[300]}`,
        borderRadius: '10px',
        mt: 2,
    },
    editFormContainer: {
        display: 'flex',
        flexDirection: 'column',
        mt: 2,
        mb: 2.375,
        ml: 5.6875,
        mr: 3.9375,
    },
    itemContainer: {
        width: '100%',
        '& .MuiFormControl-root': {
            width: '100%',
        },
        mb: 2,
    },
    customChipInputStyles: {
        width: '100%',
        '& .MuiFormControl-root': {
            width: '100%',
            minHeight: '40px',
        },
    },
    propertiesContainer: {
        width: '100%',
    },
    titleContainer: {
        width: '100%',
        mb: 2,
    },
    propertiesInputContainer: {
        width: '266.5px',
    },
    propertiesEditInputContainer: {
        width: '100%',
    },
    propertiesInputMainContainer: {
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        mb: 2,
        '& .MuiFormControl-root': {
            width: '100%',
        },
    },
    addPropertiesButtonContainer: {
        width: '100%',
    },
    addPropertiesButton: {
        borderRadius: '64px',
        width: '139px',
        height: '34px',
        '& .MuiTypography-root': {
            ...themeObject.typography.Components.fabButton,
        },
    },
    deleteButtonContainer: {
        mt: 4,
        ml: 1,
    },
    deleteButton: {
        height: '20px',
        width: '20px',
        backgroundColor: themeObject.palette.grayScale[500],
        color: themeObject.palette.background.default,
    },
    infoDisplayContainer: {
        width: '100%',
    },
    sectionLabel: {
        ...themeObject.typography.Components.inputLabel,
        color: themeObject.palette.grayScale[800],
        lineHeight: '21.17px',
        letterSpacing: '0.15px',
        mb: '0px',
    },
    sectionValue: {
        ...themeObject.typography.Components.inputText,
        color: themeObject.palette.grayScale[800],
        lineHeight: '24px',
        wordBreak: 'break-word',
        mt: '4px',
    },
    menuItemStyles: {
        height: '140px',
        '&:hover': {
            backgroundColor: alpha(themeObject.palette.grayScale[50], 1),
        },
    },
    listItemStyles: {
        textWrap: 'wrap',
        marginRight: '12px',
        '& .MuiListItemText-primary': {
            fontWeight: '500 !important',
            fontSize: '24px !important',
            lineHeight: '28.18px !important',
        },
    },
};

export const checkForDuplicatedTerm = (
    glossaryList: GlossaryMetadataItem[],
    term: string
): boolean => {
    return !!glossaryList.find((item) => item.term === term);
};

export const checkForCaseSensitiveDuplicates = (values: string[]): boolean => {
    return values.length !== new Set(values).size;
};

export const possiblySetDuplicateErrors = (
    controlName: any,
    setError: any,
    clearErrors: any,
    values: string[]
): void => {
    if (checkForCaseSensitiveDuplicates(values)) {
        setError(controlName, {
            message: glossaryValidationMessages.duplicatedOptions,
        });
    } else {
        clearErrors(controlName);
    }
};

export const createGlossaryMenuItems = [
    {
        title: 'New From Scratch',
        subtitle: 'Create a new glossary from scratch.',
        icon: AddDictionarySvg,
        navigationPath: GLOSSARY_METADATA_PATHS.CREATE_NEW_GLOSSARY_METADATA,
    },
    {
        title: 'Import Glossary',
        subtitle: 'Upload a JSON file to replace all existing entries.',
        icon: BulkGlossaryUploadIcon,
        navigationPath: GLOSSARY_METADATA_PATHS.CREATE_IMPORT_GLOSSARY_METADATA,
        styles: {
            width: '71.02px',
            height: '75.03px',
        },
    },
];
